import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { PollyLanguage } from '@shared/interfaces/polly';
import { languageFlags, languageLabels } from '../constants';
import { LanguageTab } from '../AssistantScriptDialog.styled';
import { useModalTheme } from '@shared/components/AssistantEditorModals/context';
import {
  modalThemeTokens,
  buildModalCssVars,
} from '@shared/components/AssistantEditorModals/theme';

interface LanguagePickerDialogProps {
  open: boolean;
  nonBaseLangs: PollyLanguage[];
  pickedLangs: PollyLanguage[];
  isTranslating: boolean;
  onPickedLangsChange: (langs: PollyLanguage[]) => void;
  onClose: () => void;
  onTranslate: (langs: PollyLanguage[]) => void;
}

const LanguagePickerDialog = ({
  open,
  nonBaseLangs,
  pickedLangs,
  isTranslating,
  onPickedLangsChange,
  onClose,
  onTranslate,
}: LanguagePickerDialogProps) => {
  const { t } = useTranslation('common');
  const modalTheme = useModalTheme();
  const tokens = modalThemeTokens[modalTheme];
  const cssVars = buildModalCssVars(tokens);
  const isDark = modalTheme === 'dark';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 0,
          ...(cssVars as any),
          ...(isDark && {
            background:
              'linear-gradient(135deg, rgba(28, 28, 34, 0.88) 0%, rgba(16, 16, 20, 0.88) 100%)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 18px 50px rgba(0, 0, 0, 0.45)',
            color: tokens.textPrimary,
            '& .MuiDialogTitle-root': { color: tokens.textPrimary },
            '& .MuiTypography-root': { color: tokens.textPrimary },
            '& .MuiCheckbox-root': { color: tokens.iconAccent },
            '& .MuiButton-outlined': {
              color: tokens.textPrimary,
              borderColor: tokens.cancelBorder,
              '&:hover': { borderColor: tokens.cancelHoverBorder },
            },
            '& .MuiButton-contained': {
              backgroundColor: tokens.primaryBtnBg,
              color: 'rgb(226, 228, 237)',
              '&:hover': { backgroundColor: tokens.primaryBtnHoverBg },
              '&:disabled': {
                backgroundColor: tokens.borderMid,
                color: tokens.textSecondary,
              },
            },
          }),
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: 16,
          pb: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {t('assistant.scriptDialog.chooseLanguages')}
        <FormControlLabel
          control={
            <Checkbox
              checked={pickedLangs.length === nonBaseLangs.length}
              indeterminate={
                pickedLangs.length > 0 &&
                pickedLangs.length < nonBaseLangs.length
              }
              onChange={(e) =>
                onPickedLangsChange(e.target.checked ? [...nonBaseLangs] : [])
              }
              size="small"
            />
          }
          label={<Typography sx={{ fontSize: 13 }}>{t('assistant.scriptDialog.selectAll')}</Typography>}
          sx={{ mr: 0 }}
        />
      </DialogTitle>
      <DialogContent sx={{ padding: '10px 20px !important' }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mt: 1.5,
          }}
        >
          {nonBaseLangs.map((lang) => {
            const selected = pickedLangs.includes(lang);

            return (
              <LanguageTab
                key={lang}
                active={selected}
                complete={false}
                onClick={() =>
                  onPickedLangsChange(
                    pickedLangs.includes(lang)
                      ? pickedLangs.filter((l) => l !== lang)
                      : [...pickedLangs, lang],
                  )
                }
                sx={{
                  fontSize: 15,
                  ...(selected && {
                    border: '1px solid var(--am-accent-text)',
                    '&:hover': {
                      border: '1px solid var(--am-accent-text)',
                    },
                  }),
                }}
              >
                <span>{languageFlags[lang]}</span>
                <span>{languageLabels[lang]}</span>
              </LanguageTab>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          pt: 2.5,
          gap: 1,
          justifyContent: 'space-between',
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ textTransform: 'none', borderRadius: 2, py: 1, px: 2 }}
        >
          {t('assistant.scriptDialog.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={pickedLangs.length === 0 || isTranslating}
          onClick={() => {
            onClose();
            onTranslate(pickedLangs);
          }}
          sx={{ textTransform: 'none', borderRadius: 2, py: 1, px: 2 }}
        >
          {t('assistant.scriptDialog.translate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LanguagePickerDialog;
